// ─── WebStudios content config ──────────────────────────────
// Edit prices, services, packages, FAQs and contact details here.

export const CONTACT = {
  phoneDisplay: "07440 752224",
  whatsappNumber: "447440752224",
  email: "webstudios.co.uk@gmail.com",
  whatsappMessage: "Hi WebStudios, I'd like to get a quote for a website.",
};

export const whatsappLink = (msg = CONTACT.whatsappMessage) =>
  `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(msg)}`;

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Packages", href: "#packages" },
  { label: "Our Work", href: "#work" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const SERVICES = [
  {
    id: "design",
    title: "Website Design & Development",
    desc: "A professional website designed around your business, your audience and your goals — not a generic template.",
    features: ["Custom design", "Responsive layouts", "Mobile optimisation", "Contact forms", "SEO foundations", "Deployment"],
    price: "From £249",
    note: "Larger or custom projects receive a personalised quote.",
    span: "lg:col-span-2",
  },
  {
    id: "hosting",
    title: "Website Hosting",
    desc: "Reliable, secure hosting for websites we build — and existing sites where compatible.",
    features: ["SSL included", "Backups", "Uptime monitoring", "Server management"],
    price: "£20/month",
    note: "Or £200/year — two months free.",
  },
  {
    id: "maintenance",
    title: "Website Maintenance",
    desc: "Ongoing care so you never have to touch the technical side of your website.",
    features: ["Software & security updates", "Backups", "Content updates", "Bug fixes"],
    price: "From £29/month",
    note: "Essential £29 · Business £59 · Premium £99 per month.",
  },
  {
    id: "redesign",
    title: "Website Redesign",
    desc: "For businesses with an outdated or underperforming website that needs a fresh start.",
    features: ["Modern look & feel", "Better mobile experience", "Improved performance"],
    price: "From £399",
    note: "Final price depends on website size and complexity.",
  },
  {
    id: "management",
    title: "Website Management",
    desc: "We handle your website on an ongoing basis — updates, content, support, the lot.",
    features: ["Content changes", "Technical support", "Monitoring", "Security & backups"],
    price: "From £49/month",
  },
  {
    id: "domain",
    title: "Domain & Email Setup",
    desc: "Establish a professional online identity with the right domain and business email.",
    features: ["Domain setup & DNS", "Professional email", "SSL certificate"],
    price: "From £49",
    note: "Domain registration and third-party fees may be charged separately.",
  },
  {
    id: "security",
    title: "Security & Backups",
    desc: "Security monitoring, regular backups and recovery assistance for peace of mind.",
    features: ["Security monitoring", "Scheduled backups", "Recovery assistance"],
    price: "From £19/month",
  },
  {
    id: "performance",
    title: "Performance Optimisation",
    desc: "Faster loading, better technical performance and a smoother experience for your visitors.",
    features: ["Speed improvements", "Core Web Vitals", "Technical fixes"],
    price: "From £99",
  },
];

export const PACKAGES = [
  {
    id: "starter",
    name: "Starter Website",
    price: "£249",
    tagline: "For individuals and small businesses who need a professional online presence.",
    features: ["Up to 5 pages", "Responsive design", "Mobile optimisation", "Contact form", "Basic SEO setup", "SSL certificate", "Website deployment", "1 revision round"],
    cta: "Start Your Project",
    highlight: false,
  },
  {
    id: "business",
    name: "Business Website",
    price: "£349",
    tagline: "For growing businesses that need a website built to win customers.",
    features: ["Up to 10 pages", "Custom design", "Responsive development", "Advanced contact forms", "SEO foundations", "Performance optimisation", "Analytics setup", "SSL & deployment", "Multiple revision rounds"],
    cta: "Get Started",
    highlight: true,
  },
  {
    id: "professional",
    name: "Professional",
    price: "£599",
    tagline: "For businesses that require a more advanced website.",
    features: ["Custom website build", "Advanced functionality", "Custom animations", "CMS / content management", "Advanced SEO foundations", "Integrations", "Security configuration", "Analytics", "Priority support"],
    cta: "Discuss Your Project",
    highlight: false,
  },
  {
    id: "custom",
    name: "Custom",
    price: "Let's Talk",
    tagline: "For larger or highly customised projects.",
    features: ["E-commerce stores", "Booking systems", "Membership websites", "Customer portals", "Web applications", "Complex integrations", "Custom functionality"],
    cta: "Request a Custom Quote",
    highlight: false,
  },
];

export const CARE_PLANS = [
  {
    id: "care",
    name: "Care",
    price: "£29",
    features: ["Website updates", "Security updates", "Regular backups", "Basic monitoring"],
    highlight: false,
  },
  {
    id: "care-pro",
    name: "Care Pro",
    price: "£59",
    features: ["Everything in Care", "Content changes", "Performance checks", "Priority support", "More frequent maintenance"],
    highlight: true,
  },
  {
    id: "care-premium",
    name: "Care Premium",
    price: "£99",
    features: ["Everything in Care Pro", "Priority maintenance", "Advanced monitoring", "More extensive website changes", "Priority support"],
    highlight: false,
  },
];

export const BENEFITS = [
  { title: "One place for everything", desc: "Design, hosting, domains, maintenance and support — handled by one team, under one roof." },
  { title: "Professional design", desc: "A website that makes your business look established, credible and worth choosing." },
  { title: "Reliable hosting", desc: "Secure, monitored hosting with SSL and backups — no technical headaches for you." },
  { title: "Ongoing maintenance", desc: "Updates, fixes and content changes taken care of, so your site never goes stale." },
  { title: "Security & performance", desc: "We keep your website protected, backed up and loading fast for every visitor." },
  { title: "Personal support", desc: "Talk to a real person who knows your website — including directly on WhatsApp." },
  { title: "Transparent pricing", desc: "Clear starting prices, honest quotes, and no surprise costs along the way." },
];

export const STEPS = [
  { num: "01", title: "Tell Us What You Need", desc: "Tell us about your business, your goals and what you need your website to do. A quick chat or message is enough to get started." },
  { num: "02", title: "We Design", desc: "We create a professional design tailored to your business — and refine it with your feedback until it feels right." },
  { num: "03", title: "We Build & Launch", desc: "We develop, test, optimise and launch your website, taking care of domain, hosting, SSL and everything technical." },
  { num: "04", title: "We Keep It Running", desc: "Choose ongoing hosting, maintenance and support so your website stays secure, updated and looked after." },
];

export const PORTFOLIO = [
  {
    id: "p1",
    title: "Local Trades Business",
    category: "Business Website",
    desc: "A clean, fast five-page website designed to turn local searches into phone calls and enquiries.",
    services: ["Design", "Development", "Hosting"],
    image: "https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1200",
    placeholder: true,
  },
  {
    id: "p2",
    title: "Mobile-First Service Brand",
    category: "Design & Mobile",
    desc: "A mobile-first web experience with booking enquiries, built to perform on every device.",
    services: ["Design", "Mobile optimisation", "SEO foundations"],
    image: "https://images.pexels.com/photos/270360/pexels-photo-270360.jpeg?auto=compress&cs=tinysrgb&w=1200",
    placeholder: true,
  },
  {
    id: "p3",
    title: "Digital Infrastructure Concept",
    category: "Custom Project",
    desc: "A concept exploring layered interfaces, performance-focused builds and managed hosting.",
    services: ["Development", "Hosting", "Maintenance"],
    image: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1200",
    placeholder: true,
  },
];

export const FAQS = [
  {
    q: "How much does a website cost?",
    a: "Websites start from around £249 for a professional starter site. The final price depends on the number of pages, the functionality you need and the overall complexity — use the quote calculator on this page for a quick estimate, or get in touch for a precise quote.",
  },
  {
    q: "How long does it take to build a website?",
    a: "Smaller websites can often be completed within 2–4 weeks. Larger or more complex projects — such as online stores or booking systems — typically take longer. We'll always give you a realistic timeframe before work begins, rather than promising a deadline we can't keep.",
  },
  {
    q: "Do you provide hosting?",
    a: "Yes. We offer secure, managed hosting for £20/month, or £200/year if you'd prefer to pay annually. Hosting includes SSL, backups, uptime monitoring and server management — you never have to think about it.",
  },
  {
    q: "Do you maintain websites after they are launched?",
    a: "Yes. Our maintenance plans start from £29/month and cover software updates, security updates, backups, content changes, bug fixes and general technical support. Choose from Essential, Business or Premium depending on how much support you want.",
  },
  {
    q: "Can you redesign my existing website?",
    a: "Absolutely. Website redesigns start from £399. We'll review your current site, agree what needs to change, and rebuild it into something modern, fast and mobile-friendly. The final price depends on the size and complexity of your site.",
  },
  {
    q: "Can you manage my domain?",
    a: "Yes — where you provide the necessary access or account details, we can manage your domain, DNS settings and renewals, and connect everything to your website and professional email.",
  },
  {
    q: "Do I need any technical knowledge?",
    a: "None at all. WebStudios handles the entire technical side — design, development, hosting, security and updates. You just tell us what your business needs, in plain English.",
  },
  {
    q: "Can you build online stores?",
    a: "Yes, we build e-commerce websites depending on your requirements — products, payments, delivery options and more. Because every store is different, online shops are quoted individually. Get in touch and tell us what you'd like to sell.",
  },
  {
    q: "Can you add booking systems?",
    a: "Yes. We can add booking and appointment systems depending on the system and integrations you need. Tell us how your bookings work and we'll recommend the simplest, most cost-effective setup.",
  },
  {
    q: "Do you provide SEO?",
    a: "Every website we build includes SEO foundations — proper page structure, metadata, speed and mobile optimisation. More advanced, ongoing SEO campaigns can be quoted separately depending on your goals.",
  },
  {
    q: "Can I contact you on WhatsApp?",
    a: "Yes — it's often the quickest way to reach us. Tap the green WhatsApp button on this page (or the button below) and you'll be chatting with WebStudios in seconds.",
    whatsapp: true,
  },
  {
    q: "What happens after I request a quote?",
    a: "Simple: you submit your enquiry, we review your requirements, and then we contact you to discuss the project and pricing in plain terms. Once everything is agreed, your project begins. No pressure, no obligation.",
  },
];

export const SERVICES_DROPDOWN = [
  "Website Design & Development",
  "Website Redesign",
  "Website Hosting",
  "Website Maintenance",
  "Website Management",
  "Domain & Email Setup",
  "Security & Backups",
  "Performance Optimisation",
  "E-commerce / Online Store",
  "Booking System",
  "Something else",
];

export const BUDGETS = ["Under £500", "£500 – £1,000", "£1,000 – £2,000", "£2,000+", "Not sure yet"];

export const PAYABLE_PLANS = [
  { lookup: "starter_website", name: "Starter Website", price: "£249", per: " one-off", desc: "Up to 5 pages, responsive design, contact form, basic SEO, SSL and launch — everything you need to look professional online.", tag: "" },
  { lookup: "business_website", name: "Business Website", price: "£349", per: " one-off", desc: "Up to 10 pages, custom design, advanced forms, SEO foundations, performance optimisation and analytics.", tag: "Most popular" },
  { lookup: "professional_website", name: "Professional Website", price: "£599", per: " one-off", desc: "Advanced functionality, CMS, custom animations, integrations, security configuration and priority support.", tag: "" },
  { lookup: "care_monthly", name: "Care Plan", price: "£29", per: "/month", desc: "Website updates, security updates, regular backups and basic monitoring.", tag: "" },
  { lookup: "care_pro_monthly", name: "Care Pro", price: "£59", per: "/month", desc: "Everything in Care, plus content changes, performance checks and priority support.", tag: "Most popular" },
  { lookup: "care_premium_monthly", name: "Care Premium", price: "£99", per: "/month", desc: "Everything in Care Pro, plus priority maintenance and advanced monitoring.", tag: "" },
  { lookup: "hosting_monthly", name: "Website Hosting", price: "£20", per: "/month", desc: "Secure managed hosting with SSL, backups and uptime monitoring.", tag: "" },
  { lookup: "hosting_yearly", name: "Hosting — Annual", price: "£200", per: "/year", desc: "A full year of managed hosting — two months free versus paying monthly.", tag: "Best value" },
  { lookup: "security_monthly", name: "Security & Backups", price: "£19", per: "/month", desc: "Security monitoring, scheduled backups and recovery assistance.", tag: "" },
  { lookup: "management_monthly", name: "Website Management", price: "£49", per: "/month", desc: "Fully hands-off website care — updates, content changes, monitoring, security and hosting management.", tag: "" },
];

export const CARE_LOOKUP = { care: "care_monthly", "care-pro": "care_pro_monthly", "care-premium": "care_premium_monthly" };

export const PACKAGE_LOOKUP = { starter: "starter_website", business: "business_website", professional: "professional_website" };

export const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: August 2026",
    intro: "WebStudios (\"we\", \"us\") respects your privacy. This policy explains what information we collect through this website, why we collect it, and the rights you have over it under UK data protection law (UK GDPR).",
    sections: [
      { h: "What we collect", body: [
        "Enquiry form: your name, email address, phone number, business name, current website address, the service you're interested in, your budget and your project description.",
        "AI assistant: the messages you send to our website chat assistant. If you choose to share your name and email in chat, we store them so we can follow up with you.",
        "Analytics: we use privacy-respecting product analytics to understand how the site is used (pages visited, general device information). We do not sell or share this data with advertisers.",
      ]},
      { h: "Why we collect it", body: [
        "To respond to your enquiry, prepare a quote, and deliver the services you ask for.",
        "To improve our website and the answers our AI assistant gives.",
        "We will never sell your personal information or use it for anything unrelated to WebStudios.",
      ]},
      { h: "How it's stored and who processes it", body: [
        "Your information is stored securely in our database and processed only to handle your enquiry or conversation.",
        "We use trusted third-party processors to run this website (hosting, email delivery and AI providers). They process data only on our instructions.",
        "We keep enquiry records for as long as needed to handle your project and any follow-up, after which they are deleted or anonymised.",
      ]},
      { h: "Your rights", body: [
        "You can ask us at any time for a copy of the personal data we hold about you, ask us to correct it, or ask us to delete it.",
        "To exercise any of these rights, email webstudios.co.uk@gmail.com or message us on WhatsApp at 07440 752224 and we'll respond promptly.",
        "If you're unhappy with how we handle your data, you have the right to complain to the UK Information Commissioner's Office (ICO).",
      ]},
      { h: "How long we keep your data", body: [
        "Enquiry and conversation records are kept for as long as needed to handle your project, provide support and meet any legal or accounting obligations — after which they are deleted or anonymised.",
        "If an enquiry doesn't lead to a project, we may keep it for a short period in case you return, then remove it.",
      ]},
      { h: "Data security", body: [
        "Access to enquiry and customer information is restricted to the people who need it to run WebStudios, and our systems are protected with authentication and encryption in transit.",
        "No method of storage or transmission is perfectly secure, but we take reasonable, proportionate measures to protect your information and will tell you promptly if a breach affects you.",
      ]},
      { h: "Changes to this policy", body: [
        "If we change how we handle personal data, we'll update this page and change the date at the top. Material changes will be highlighted where practical.",
        "This policy was written to be read by humans, not lawyers — if anything is unclear, just ask.",
      ]},
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: August 2026",
    intro: "These terms apply when you use this website or engage WebStudios for website design, development, hosting, maintenance or related services.",
    sections: [
      { h: "Quotes and pricing", body: [
        "Prices shown on this website are \"starting from\" prices unless explicitly stated otherwise. Final quotes depend on your project's pages, features and complexity, and are confirmed in writing before work begins.",
        "The online estimate calculator provides a guide only — it is not a binding quote.",
        "Third-party costs (for example domain registration, premium plugins or stock licences) may be charged separately and will always be flagged in advance.",
      ]},
      { h: "Projects and payment", body: [
        "A project begins once the scope and price are agreed in writing. Payment terms (deposit, milestones and final payment) are set out in your individual agreement.",
        "You agree to provide the content, feedback and access we reasonably need to complete your project. Delays in providing these may extend the timeline.",
        "Timescales we give are realistic estimates, not guaranteed deadlines.",
      ]},
      { h: "Hosting, maintenance and care plans", body: [
        "Monthly services (hosting, maintenance, management, security) are billed in advance and can be cancelled with notice as set out in your agreement.",
        "We take reasonable care to keep hosted websites secure, backed up and available, but we do not guarantee uninterrupted uptime or that any website is completely immune to security incidents.",
        "Care plans cover the tasks listed for each tier. Work outside the plan scope may be quoted separately.",
      ]},
      { h: "Intellectual property and liability", body: [
        "Once your project is paid for in full, the website design and code we created for you are yours. We may reuse general techniques and know-how.",
        "You confirm that content you supply (text, images, logos) doesn't infringe anyone else's rights.",
        "Nothing in these terms excludes liability that cannot be excluded by law. Otherwise, our liability is limited to the amount you have paid us for the relevant service.",
      ]},
      { h: "Contact", body: [
        "Questions about these terms: email webstudios.co.uk@gmail.com or WhatsApp 07440 752224.",
      ]},
      { h: "Cancellations and refunds", body: [
        "Monthly plans (hosting, maintenance, management, security) can be cancelled at any time by contacting us — cancellation stops future payments, and services continue until the end of the paid period.",
        "For project work, if you cancel after work has begun, payment is due for the work completed up to that point. Deposits cover time already reserved and are normally non-refundable once work has started.",
        "If we ever fail to deliver an agreed service, we'll always put it right or refund the affected payment — fairly and without a fight.",
      ]},
      { h: "Your responsibilities", body: [
        "You are responsible for the accuracy of the information you give us and for having the rights to any content (text, images, logos) you provide for your website.",
        "You are responsible for keeping any login details we hand over secure, and for telling us promptly if anything on your website needs correcting for legal or regulatory reasons in your industry.",
      ]},
      { h: "Governing law and changes", body: [
        "These terms are governed by the laws of England and Wales, and any disputes will be handled by the courts of England and Wales.",
        "We may update these terms from time to time; the version on this page applies to new agreements from the date shown above. Existing agreements keep the terms they were made under unless we agree otherwise together.",
      ]},
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Last updated: September 2026",
    intro: "This policy explains how the WebStudios website uses cookies and similar technologies — what they are, why we use them, and how you can control them. We keep our use of cookies deliberately minimal: this website exists to tell you about our services and take enquiries, not to track you around the internet.",
    sections: [
      { h: "What cookies and similar technologies are", body: [
        "Cookies are small text files a website stores on your device. Similar technologies include browser local storage, which works in much the same way. They are commonly used to make websites function, remember preferences and understand how pages are used.",
        "Like most websites, we use a small number of these technologies to run this site properly. We do not use advertising cookies, and we do not build marketing profiles of our visitors.",
      ]},
      { h: "Essential storage", body: [
        "We store a small identifier in your browser's local storage so the AI assistant can remember your conversation during your visit. Without it, the chat would forget everything each time you sent a message. This identifier contains no personal details unless you choose to type them into the chat.",
        "A similar small item remembers your admin session if you sign in to the owner dashboard. These are strictly necessary for those features to work and are not used for any other purpose.",
      ]},
      { h: "Analytics", body: [
        "We use privacy-respecting product analytics to understand which pages are visited and how the site performs — for example, whether visitors find the pricing page or how quickly pages load. This helps us improve the website.",
        "Analytics information is aggregated and is not used for advertising, is not sold, and is not shared with advertisers. It may include general device information such as browser type and approximate region.",
      ]},
      { h: "Third-party content", body: [
        "Some pages load fonts from Google Fonts and Fontshare, and images from stock photo providers. These services may set their own cookies or record technical request information (such as your IP address) when your browser fetches those files. We choose providers with strong privacy reputations, but their own policies apply to those requests.",
        "Payments are handled by Stripe on their secure checkout page. Stripe may set cookies on that page for fraud prevention and payment security — these are governed by Stripe's own cookie policy.",
      ]},
      { h: "Managing cookies", body: [
        "You can control or delete cookies and local storage through your browser settings at any time — every modern browser lets you block or clear them, usually under Privacy or Site Settings.",
        "Blocking essential storage will stop the chat assistant from remembering your conversation and will sign you out of the admin dashboard, but the rest of the website will continue to work normally.",
        "If you have any questions about how this website uses cookies, email webstudios.co.uk@gmail.com or message us on WhatsApp at 07440 752224.",
      ]},
    ],
  },
};
